import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, type HoldingsResponse } from '../lib/api.js';
import { formatCompact } from './MoneyText.js';

/**
 * ⌘K — type a fund, land on its expanded row.
 *
 * With 69 holdings, typing three letters beats any amount of scrolling. The
 * palette reuses the holdings cache (no extra request when Wealth has loaded),
 * matches on fund, member and folio, and navigates to /wealth?open=<id> so the
 * row arrives already expanded — the URL contract the list already honours.
 *
 * Deliberately small: no actions, no pages, no history. It answers exactly one
 * question — "where is my money in X?" — and disappears.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data } = useQuery({
    queryKey: ['holdings', null, false, 'type', 'name'],
    queryFn: () => api.get<HoldingsResponse>('/holdings'),
    enabled: open,
    staleTime: 60_000,
  });

  const items = useMemo(() => {
    const all = (data?.sections ?? []).flatMap((s) => s.holdings).filter((h) => h.scheme.type !== 'epf');
    const needle = q.trim().toLowerCase();
    if (!needle) return all.slice(0, 8);
    return all
      .filter((h) =>
        `${h.scheme.name} ${h.scheme.fullName} ${h.member.name} ${h.folio}`.toLowerCase().includes(needle),
      )
      .slice(0, 12);
  }, [data, q]);

  useEffect(() => {
    if (open) {
      setQ('');
      setSel(0);
      // Focus after the overlay mounts; autoFocus misses on re-open.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => setSel(0), [q]);

  if (!open) return null;

  function go(id: string) {
    onClose();
    navigate(`/wealth?open=${id}`);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') onClose();
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => Math.min(s + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter' && items[sel]) {
      go(items[sel]!.id);
    }
  }

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Find a fund" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="cmdk-input"
          placeholder="Find a fund, member or folio…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          role="combobox"
          aria-expanded="true"
          aria-controls="cmdk-results"
        />
        <div className="cmdk-list" id="cmdk-results" role="listbox">
          {items.length === 0 && <div className="cmdk-empty">Nothing matches “{q}”.</div>}
          {items.map((h, i) => (
            <button
              key={h.id}
              type="button"
              role="option"
              aria-selected={i === sel}
              className="cmdk-item"
              onPointerEnter={() => setSel(i)}
              onClick={() => go(h.id)}
            >
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {h.scheme.name}
                <small style={{ display: 'block' }}>
                  {h.member.name} · {h.folio}
                </small>
              </span>
              <small style={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {h.valuation ? formatCompact(h.valuation.money.amount) : '—'}
              </small>
            </button>
          ))}
        </div>
        <div className="cmdk-hint">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
